import { useId, useState, type FormEvent } from 'react';
import { useLocation } from 'wouter';
import { useTranslation } from 'react-i18next';
import '@/pages/landing/home/home.css';
import { clearSignupEmail, looksLikeEmail, storeSignupEmail } from '@/lib/signup-email';

export const REGISTER_PATH = '/register';

interface SignupFormProps {
  /** Compact variant of the nav bar (the input is hidden on phones, only the button stays). */
  compact?: boolean;
  className?: string;
  /** Outside the landing page: wraps the form in the landing's own style scope (.cc-home), transparent. */
  scoped?: boolean;
}

/**
 * Email + "Start free". A valid-looking address is handed to the registration form through
 * sessionStorage (never the URL); an empty or invalid one just opens the registration form.
 */
export function SignupForm({ compact = false, className = '', scoped = false }: SignupFormProps) {
  const { t } = useTranslation('home');
  const [, navigate] = useLocation();
  const [email, setEmail] = useState('');
  const inputId = useId();

  const onSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const value = email.trim();
    if (looksLikeEmail(value)) storeSignupEmail(value);
    else clearSignupEmail();
    navigate(REGISTER_PATH);
  };

  const form = (
    <form
      className={`signup${compact ? ' sm' : ''} ${className}`.trim()}
      onSubmit={onSubmit}
      noValidate
    >
      <label htmlFor={inputId} className="sr-only">
        {t('landing.signup.emailLabel')}
      </label>
      <input
        id={inputId}
        type="email"
        name="email"
        inputMode="email"
        autoComplete="email"
        placeholder={t('landing.signup.emailPlaceholder')}
        value={email}
        onChange={(e) => setEmail(e.target.value)}
      />
      <button className={`btn btn-y${compact ? ' btn-sm' : ''}`} type="submit">
        {t('landing.signup.cta')}
      </button>
    </form>
  );
  return scoped ? <div className="cc-home signup-scope">{form}</div> : form;
}
